import React, { Component, useState } from "react";
import Config from "../../../json/config.json";

import sha256 from "sha256";

export function getJWT() { }

// Reads an on/off template setting that may have come back from the backend
// as a string or number ("true"/"True"/"1"/1) instead of a boolean. Anything
// missing or unrecognised falls back to `defaultValue`.
// Fonts available in the editor (loaded in src/index.css).
export const FONT_FAMILIES = ["Inter", "Times New Roman", "Lato", "Montserrat", "Open Sans", "Roboto", "Arimo"];

// ---- Text style in saved HTML ------------------------------------------
// Same precedence as the editor canvas: the element's own setting, then the
// section's default font (`<section>.font`), then the template default
// (`prop.fontColor` / `prop.fontSize`). Used by the saved-HTML templates so
// the PDF matches what the user sees in the editor.
export function resolveTextStyle(style, sectionFont, prop, options = {}) {
  const resolved = { ...(style || {}) };
  resolved.fontFamily = resolved.fontFamily || sectionFont?.fontFamily || "Inter";
  if (!resolved.color) {
    resolved.color = (options.ignoreSectionColor ? null : sectionFont?.color) || prop?.fontColor;
  }
  if (!resolved.fontSize) {
    resolved.fontSize = options.defaultFontSize
      || (sectionFont?.fontSize ? sectionFont.fontSize + "pt" : (prop?.fontSize ?? 10) + "pt");
  }
  return resolved;
}

// ---- Items table styling --------------------------------------------------
// Whole-table styles live in `items.layout.tableHeader` (headings) and
// `items.layout.itemRow` (values). Each column can override any of these
// fields in `column.headerStyle` / `column.rowStyle`; unset fields follow the
// whole table.

export const TABLE_TEXT_STYLE_FIELDS = [
  "backgroundColor", "fontFamily", "fontSize", "fontColor", "textAlign",
  "bold", "italic", "underline", "textTransform",
];

// The B / I / U / Aa / AB buttons, reset together.
export const TABLE_TEXT_STYLE_GROUP = ["bold", "italic", "underline", "textTransform"];

const isStyleSet = (value) => value !== undefined && value !== null && value !== "";

// part: "header" | "row". `column` may be null for the whole-table value.
export function resolveTableCellStyle(layout, column, part) {
  const table = (part === "header" ? layout?.tableHeader : layout?.itemRow) || {};
  const override = (part === "header" ? column?.headerStyle : column?.rowStyle) || {};
  const resolved = {};
  TABLE_TEXT_STYLE_FIELDS.forEach((field) => {
    resolved[field] = isStyleSet(override[field]) ? override[field] : table[field];
  });
  return resolved;
}

// CSS for a header or value cell from a resolved style.
export function tableCellCss(resolved) {
  const transform = resolved?.textTransform;
  return {
    backgroundColor: resolved?.backgroundColor,
    fontFamily: resolved?.fontFamily,
    fontSize: isStyleSet(resolved?.fontSize) ? `${resolved.fontSize}pt` : undefined,
    color: resolved?.fontColor,
    textAlign: resolved?.textAlign || "left",
    fontWeight: resolved?.bold ? "bold" : "normal",
    fontStyle: resolved?.italic ? "italic" : "normal",
    textDecoration: resolved?.underline ? "underline" : "none",
    textTransform: transform === "capitalize" || transform === "uppercase" ? transform : "none",
  };
}

// Background of a value cell. With `itemRow.stripeRows` on, even rows (the
// 2nd, 4th, …; rowIndex 1, 3, …) use `itemRow.evenRowBackgroundColor` — except
// in a column that has its own background, which keeps it on every row.
export function getRowCellBackground(layout, column, rowIndex) {
  const row = layout?.itemRow || {};
  const resolved = resolveTableCellStyle(layout, column, "row");
  const isEvenRow = rowIndex % 2 === 1;
  const columnHasOwnBackground = isStyleSet(column?.rowStyle?.backgroundColor);
  if (isEvenRow && row.stripeRows && !columnHasOwnBackground) {
    return row.evenRowBackgroundColor || resolved.backgroundColor;
  }
  return resolved.backgroundColor;
}

// Class for an underlined cell: keeps the underline while hovered (the app
// removes text-decoration on :hover globally).
export function getUnderlineCellClass(resolved) {
  return resolved?.underline ? "column-underline" : undefined;
}

// Placeholder text for a column cell in the editor: lower-case words, so
// bold/italic/underline/capitalize/uppercase all visibly change it.
export function getColumnSampleText(column) {
  const words = String(column?.header || column?.data || "value")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return "sample " + (words || "value");
}

// ---- Column value formats -------------------------------------------------
// Item table columns can set `format` (chosen in the Items > Labels tab):
//   { type: "text" | "number" | "currency" | "percentage",
//     decimals, thousandsSeparator, currencyPosition: "before" | "after",
//     negativeStyle: "minus" | "brackets", hideZero }
// Older templates only carry `showCurrency`; those read as a currency format.
// `showCurrency` is still kept in sync on save so existing consumers work.
export const COLUMN_FORMAT_TYPES = [
  { value: "text", label: "Text" },
  { value: "number", label: "Number" },
  { value: "currency", label: "Currency" },
  { value: "percentage", label: "Percentage" },
];

const COLUMN_FORMAT_DEFAULTS = {
  text: {},
  number: { decimals: 0, thousandsSeparator: true },
  currency: { decimals: 2, thousandsSeparator: true, currencyPosition: "before" },
  percentage: { decimals: 2, thousandsSeparator: false },
};

export function getColumnFormat(column) {
  const stored = column?.format || {};
  const type = stored.type || (column?.showCurrency ? "currency" : "text");
  return {
    negativeStyle: "minus",
    hideZero: false,
    ...(COLUMN_FORMAT_DEFAULTS[type] || {}),
    ...stored,
    type,
  };
}

export function isNumericColumnFormat(format) {
  return !!format && format.type !== "text";
}

// Formats a raw value for a column. Non-numeric values and "text" columns are
// returned unchanged.
export function formatColumnValue(value, column, currencySymbol) {
  const format = getColumnFormat(column);
  if (!isNumericColumnFormat(format)) return value;

  const n = typeof value === "number" ? value : parseFloat(value);
  if (value === "" || value === null || value === undefined || isNaN(n)) return value;
  if (format.hideZero && n === 0) return "";

  const decimals = Math.min(Math.max(parseInt(format.decimals, 10) || 0, 0), 6);
  let text = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    useGrouping: !!format.thousandsSeparator,
  }).format(Math.abs(n));

  if (format.type === "percentage") text += "%";
  if (format.type === "currency" && currencySymbol) {
    text = format.currencyPosition === "after" ? text + " " + currencySymbol : currencySymbol + text;
  }

  if (n < 0) text = format.negativeStyle === "brackets" ? "(" + text + ")" : "-" + text;
  return text;
}

// ---- Lists --------------------------------------------------------------
// Bulleted/numbered text elements can set `listSpacing` (px): extra space
// between the bullet/number and the text of each line. Lists without one use
// DEFAULT_LIST_SPACING; the editor saves it into the template.
export const DEFAULT_LIST_SPACING = 5;

export function getListSpacing(element) {
  const n = parseFloat(element?.listSpacing);
  if (isNaN(n)) return DEFAULT_LIST_SPACING;
  return n < 0 ? 0 : n;
}

// ---- Colours in saved HTML ----------------------------------------------
// Colours are stored as #RRGGBBAA. Browsers understand that, but many
// HTML-to-PDF engines don't, so the saved HTML uses rgba() for anything
// semi-transparent and plain #RRGGBB for fully opaque colours.

export function hexAlphaToCss(hex) {
  const m = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/i.exec(hex);
  if (!m) return hex;
  const [r, g, b, a] = m.slice(1).map((h) => parseInt(h, 16));
  if (a === 255) return "#" + m[1] + m[2] + m[3];
  return `rgba(${r}, ${g}, ${b}, ${Math.round((a / 255) * 1000) / 1000})`;
}

// Rewrites every #RRGGBBAA inside style="…" attributes of an HTML string.
export function convertHtmlAlphaColors(html) {
  if (!html) return html;
  return html.replace(/style="([^"]*)"/g, (match, css) =>
    'style="' + css.replace(/#[0-9a-fA-F]{8}(?![0-9a-fA-F])/g, hexAlphaToCss) + '"'
  );
}

// ---- Shape borders ------------------------------------------------------
// Box/circle shapes have one border colour, thickness and style for all
// sides (`borderColor`, `borderWidth`, `borderStyle`), optional per-side
// overrides in `borderSides.{top,right,bottom,left}.{color,width,style}`, and
// fall back to the section's `shapes.border` defaults. Lines use
// `borderColor` + `borderStyle` (their thickness is their height).

export const SHAPE_BORDER_SIDES = ["top", "right", "bottom", "left"];

export const SHAPE_BORDER_STYLES = [
  { value: "solid", label: "Solid" },
  { value: "dashed", label: "Dashed" },
  { value: "dotted", label: "Dotted" },
];

const toBorderStyle = (value, fallback = "solid") => (
  SHAPE_BORDER_STYLES.some((s) => s.value === value) ? value : fallback
);

const toPx = (value, fallback) => {
  const n = parseFloat(value);
  return isNaN(n) || n < 0 ? fallback : n;
};

// Colour/width/style of one side (or "all") after overrides and defaults.
export function getShapeBorderSide(shape, sectionBorder, side) {
  const baseColor = shape?.borderColor || sectionBorder?.color || "#333333FF";
  const baseWidth = toPx(shape?.borderWidth, toPx(sectionBorder?.size, 1));
  const baseStyle = toBorderStyle(shape?.borderStyle);
  if (!side || side === "all") return { color: baseColor, width: baseWidth, style: baseStyle };

  const override = shape?.borderSides?.[side] || {};
  return {
    color: override.color || baseColor,
    width: toPx(override.width, baseWidth),
    style: toBorderStyle(override.style, baseStyle),
  };
}

// CSS for a box/circle shape's four borders.
export function getShapeBorderStyle(shape, sectionBorder) {
  const style = {};
  SHAPE_BORDER_SIDES.forEach((side) => {
    const { color, width, style: lineStyle } = getShapeBorderSide(shape, sectionBorder, side);
    const key = "border" + side.charAt(0).toUpperCase() + side.slice(1);
    style[key] = width > 0 ? `${width}px ${lineStyle} ${color}` : "none";
  });
  return style;
}

// CSS for a line shape. Solid lines are a filled bar; dashed/dotted lines are
// drawn as a top border as thick as the line, so they look the same height.
export function getLineShapeStyle(shape, sectionBorder) {
  const color = shape?.borderColor || sectionBorder?.color || "#333333FF";
  const lineStyle = toBorderStyle(shape?.borderStyle);
  if (lineStyle === "solid") return { border: "none", background: color };

  const thickness = toPx(shape?.style?.height, 2) || 2;
  return {
    border: "none",
    borderTop: `${thickness}px ${lineStyle} ${color}`,
    background: "transparent",
    boxSizing: "border-box",
  };
}

// ---- Page numbers -------------------------------------------------------
// Printed on every page of the PDF; configured in Properties
// (`prop.pageNumber`) and shown as a marker on the editor canvas.

export const PAGE_NUMBER_POSITIONS = [
  { value: "topLeft", label: "Top left" },
  { value: "topCenter", label: "Top middle" },
  { value: "topRight", label: "Top right" },
  { value: "bottomLeft", label: "Bottom left" },
  { value: "bottomCenter", label: "Bottom middle" },
  { value: "bottomRight", label: "Bottom right" },
];

export const PAGE_NUMBER_FORMATS = [
  { value: "pageOfTotal", template: "Page {page} of {total}" },
  { value: "page", template: "Page {page}" },
  { value: "slash", template: "{page} / {total}" },
  { value: "number", template: "{page}" },
];

export const PAGE_NUMBER_DEFAULTS = {
  show: true,
  position: "bottomRight",
  format: "pageOfTotal",
  fontSize: 8,
  color: "#333333FF",
};

// Normalised page-number settings: fills in defaults for templates saved
// before the setting existed and ignores unknown values.
export function getPageNumberSettings(prop) {
  const raw = prop?.pageNumber || {};
  const fontSize = parseInt(raw.fontSize, 10);

  return {
    show: isFlagOn(raw.show, PAGE_NUMBER_DEFAULTS.show),
    position: PAGE_NUMBER_POSITIONS.some((p) => p.value === raw.position) ? raw.position : PAGE_NUMBER_DEFAULTS.position,
    format: PAGE_NUMBER_FORMATS.some((f) => f.value === raw.format) ? raw.format : PAGE_NUMBER_DEFAULTS.format,
    fontSize: fontSize >= 1 ? fontSize : PAGE_NUMBER_DEFAULTS.fontSize,
    color: raw.color || prop?.fontColor || PAGE_NUMBER_DEFAULTS.color,
  };
}

export function formatPageNumber(format, page, total) {
  const entry = PAGE_NUMBER_FORMATS.find((f) => f.value === format) || PAGE_NUMBER_FORMATS[0];
  return entry.template.replace("{page}", page).replace("{total}", total);
}

// CSS placement inside a page: 0.15in from the top/bottom edge and 0.3in
// from the left/right edge; "Center" is centred horizontally. All four sides
// are always set (unused ones to "auto") so page-level CSS such as
// `.canves-content > div { top: 0; right: 0; bottom: 0; left: 0 }` can't
// stretch the element or pin it to the wrong edge.
export function getPageNumberPositionStyle(position) {
  const pos = PAGE_NUMBER_POSITIONS.some((p) => p.value === position) ? position : PAGE_NUMBER_DEFAULTS.position;

  const style = { top: "auto", right: "auto", bottom: "auto", left: "auto", transform: "none" };
  if (pos.startsWith("top")) style.top = "0.15in";
  else style.bottom = "0.15in";

  if (pos.endsWith("Left")) style.left = "0.3in";
  else if (pos.endsWith("Right")) style.right = "0.3in";
  else {
    style.left = "50%";
    style.transform = "translateX(-50%)";
  }
  return style;
}

// Full inline style for a page number (canvas marker and preview).
export function getPageNumberStyle(prop, footFont) {
  const settings = getPageNumberSettings(prop);
  return {
    position: "absolute",
    ...getPageNumberPositionStyle(settings.position),
    fontSize: settings.fontSize + "pt",
    fontFamily: footFont?.fontFamily || "Inter",
    color: settings.color,
    whiteSpace: "nowrap",
  };
}

// Bands mode: the page number sits in a corner (or the middle) of the area
// the bands are laid out in, so it stays inside a page border. `area` and
// `page` are px boxes from getPageLayout (lib/bands/bandModel).
export function getBandsPageNumberStyle(prop, footFont, area, page) {
  const settings = getPageNumberSettings(prop);
  const style = {
    position: "absolute",
    top: "auto",
    right: "auto",
    bottom: "auto",
    left: "auto",
    transform: "none",
    fontSize: settings.fontSize + "pt",
    fontFamily: footFont?.fontFamily || "Inter",
    color: settings.color,
    whiteSpace: "nowrap",
  };
  if (settings.position.startsWith("top")) style.top = area.top + "px";
  else style.bottom = page.height - (area.top + area.height) + "px";

  if (settings.position.endsWith("Left")) style.left = area.left + "px";
  else if (settings.position.endsWith("Right")) style.right = page.width - (area.left + area.width) + "px";
  else {
    style.left = area.left + area.width / 2 + "px";
    style.transform = "translateX(-50%)";
  }
  return style;
}

export function isFlagOn(value, defaultValue = false) {
  if (value === true || value === false) return value;
  if (value === 1 || value === 0) return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    if (v === "true" || v === "1") return true;
    if (v === "false" || v === "0" || v === "") return false;
  }
  return defaultValue;
}

const PAGE_SETTINGS_PX = {
  a4: { width: 794, height: 1123 },
  letter: { width: 595, height: 842 }
};

// Real, in-px usable area of a section (head/total/foot), derived from the
// page's paper size/orientation and margins, so newly placed elements can be
// kept within the section's actual rendered bounds instead of an arbitrary box.
export function getSectionBoundsPx(prop, heights, sectionKey) {
  const paperSize = (prop && prop.paperSize ? prop.paperSize : "a4").toString().toLowerCase();
  const dims = PAGE_SETTINGS_PX[paperSize] || PAGE_SETTINGS_PX.a4;
  const isLandscape = prop && prop.orientation === "landscape";
  const pageWidthPx = isLandscape ? dims.height : dims.width;
  const pageHeightPx = isLandscape ? dims.width : dims.height;

  const marginLeftPx = ((prop && prop.margin && prop.margin.left) || 0) * 96;
  const marginRightPx = ((prop && prop.margin && prop.margin.right) || 0) * 96;

  const sectionHeightPct = (heights && heights[sectionKey] != null ? heights[sectionKey] : 10) / 100;

  return {
    width: Math.max(pageWidthPx - marginLeftPx - marginRightPx, 100),
    height: Math.max(pageHeightPx * sectionHeightPct, 60)
  };
}

// Prepends `symbol` to an element's own text when that specific element's
// `showCurrency` flag is on (set per-element via the toolbar's Currency
// checkbox). The stored element text itself is never mutated — only what's
// rendered here — so the canvas editor stays silent about it (no literal
// "{CurrencySymbol}" text) and this is purely a display concern. The saved
// template only carries the `showCurrency` flag; the real, dynamic currency
// symbol substitution for the exported document is the backend's job.
export function applyCurrencyPrefix(text, data, symbol) {
  if (!data || !data.showCurrency || !text || !symbol) return text;

  return symbol + text;
}

// ---- Badges and value colour rules --------------------------------------
// A text element can be drawn as a badge (a box behind its text) and can
// change colour depending on the value printed in it, e.g. a status that is
// teal for "Confirmed" and red for "Cancelled". Stored on the element as
// `badge`:
//   enabled                                   draw the box
//   paddingX, paddingY, borderRadius, borderWidth   box shape, in px
//   default   { background, borderColor, color }    when no rule matches
//   rules     [{ value, background, borderColor, color }]
// The first rule whose `value` matches the printed text wins (ignoring case
// and surrounding spaces); a colour left empty on a rule falls back to
// `default`, and an empty text colour to the element's own font colour.
// Rules still change the text colour when the box is off. The PDF backend
// reads all of this from the template JSON, never from the saved HTML.
export const BADGE_DEFAULTS = {
  enabled: false,
  paddingX: 8,
  paddingY: 2,
  borderRadius: 10,
  borderWidth: 1,
  default: { background: "#F3F4F6FF", borderColor: "#D1D5DBFF", color: "" },
};

export function getBadgeSettings(element) {
  const raw = element?.badge || {};
  const size = (value, fallback) => {
    const n = parseFloat(value);
    return n >= 0 ? n : fallback;
  };

  return {
    enabled: isFlagOn(raw.enabled, BADGE_DEFAULTS.enabled),
    paddingX: size(raw.paddingX, BADGE_DEFAULTS.paddingX),
    paddingY: size(raw.paddingY, BADGE_DEFAULTS.paddingY),
    borderRadius: size(raw.borderRadius, BADGE_DEFAULTS.borderRadius),
    borderWidth: size(raw.borderWidth, BADGE_DEFAULTS.borderWidth),
    default: { ...BADGE_DEFAULTS.default, ...(raw.default || {}) },
    rules: Array.isArray(raw.rules) ? raw.rules.filter((r) => r && typeof r === "object") : [],
  };
}

const normaliseBadgeValue = (value) => (value ?? "").toString().trim().toLowerCase();

export function findBadgeRule(element, text) {
  const target = normaliseBadgeValue(text);
  if (!target) return null;
  return getBadgeSettings(element).rules.find((r) => normaliseBadgeValue(r.value) === target) || null;
}

// Colours for `text` printed in `element`: { background, borderColor, color }
// with the box on, { color } when only a rule recolours the text, or null.
export function resolveBadgeColors(element, text) {
  const settings = getBadgeSettings(element);
  const rule = findBadgeRule(element, text);

  if (!settings.enabled) return rule?.color ? { color: rule.color } : null;

  return {
    background: rule?.background || settings.default.background,
    borderColor: rule?.borderColor || settings.default.borderColor,
    color: rule?.color || settings.default.color || "",
  };
}

// Inline style for the box drawn around the text, or null when the badge is
// off. It fits the text plus padding; the element's textAlign places it.
export function getBadgeBoxStyle(element, text) {
  const settings = getBadgeSettings(element);
  if (!settings.enabled) return null;

  const colors = resolveBadgeColors(element, text);
  return {
    display: "inline-block",
    maxWidth: "100%",
    boxSizing: "border-box",
    padding: `${settings.paddingY}px ${settings.paddingX}px`,
    borderRadius: settings.borderRadius + "px",
    border: settings.borderWidth > 0 ? `${settings.borderWidth}px solid ${colors.borderColor}` : "none",
    backgroundColor: colors.background,
    ...(colors.color ? { color: colors.color } : {}),
  };
}

// Repairs a keyed map (e.g. section.elements / section.companyProps) that may
// contain leftover damage from a historical bug: entries stored under a key
// that doesn't match `value.name` with all dots stripped (e.g. multi-level
// paths like "A.B.City" got flattened inconsistently in different places),
// plus empty "{}" phantom entries a failed move once wrote under the
// mismatched key. Re-keys real entries correctly and drops phantom ones.
// Returns the original map unchanged if nothing needed fixing.
export function repairKeyedElementMap(map) {
  if (!map) return map;

  const repaired = {};
  let changed = false;

  Object.keys(map).forEach((key) => {
    const value = map[key];

    if (!value || (!value.name && !value.style && !value.text)) {
      changed = true;
      return;
    }

    const correctKey = value.name ? value.name.toString().replace(/\./g, "") : key;

    if (repaired[correctKey]) {
      changed = true;
      if (!repaired[correctKey].style && value.style) {
        repaired[correctKey] = value;
      }
      return;
    }

    repaired[correctKey] = value;
    if (correctKey !== key) changed = true;
  });

  return changed ? repaired : map;
}

export function stringFormat(str, data) {
  Object.keys(data).forEach(function (key) {
    var regEx = new RegExp("\\{" + key + "\\}", "gm");
    str = str.replace(regEx, data[key]);
  });

  return str;
}

export function encrypt(data) {
  var CryptoJS = require("crypto-js");
  var key = Config.CryptKey;
  var cipherText = CryptoJS.AES.encrypt(JSON.stringify(data), key).toString();
  return cipherText;
}

export function decrypt(data) {
  var CryptoJS = require("crypto-js");
  var key = Config.CryptKey;
  var bytes = CryptoJS.AES.decrypt(data, key);
  var decText = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  return decText;
}

export function createElement(name) {
  var names = name.toString().split(".");
  var id = sha256(name + "-ele-" + Date.now());

  return {
    name: name,
    text: "{" + names[names.length - 1] + "}",
    data: names[names.length - 1],
    id: id,
    style: {
      position: "absolute",
      top: "40px",
      left: "40px",
      zIndex: 1
      /*
      fontFamily: "Helvetica",
      color: "#333333FF",
      fontSize: "12pt",*/
    },
  };
}

export function createShape(name) {
  var names = name.toString().split(".");
  var id = sha256(name + "-ele-" + Date.now());

  return {
    name: name,
    text: "{" + names[names.length - 1] + "}",
    data: names[names.length - 1],
    id: id,
    style: {
      position: "absolute",
      top: "40px",
      left: "40px",
      borderRadius: name.includes('circle') ? '50%' : 0,
      zIndex: 0
      /*
      fontFamily: "Helvetica",
      color: "#333333FF",
      fontSize: "12pt",*/
    },
  };
}

export function createHorizontalLine(name) {
  var names = name.toString().split(".");
  var id = sha256(name + "-ele-" + Date.now());

  return {
    name: name,
    text: "{" + names[names.length - 1] + "}",
    data: names[names.length - 1],
    id: id,
    style: {
      position: "absolute",
      top: "40px",
      left: "40px",
      width: "150px",
      height: "2px",
      borderRadius: 0,
      zIndex: 0
    },
  };
}

export function createQRCode(name) {
  var id = sha256(name + "-ele-" + Date.now());

  return {
    name: name,
    qrMode: "generate", // "generate" | "image" | "field"
    qrValue: "",
    qrImage: "",
    qrFieldPath: "", // full dotted path, e.g. "EposSalesDefinition.InvoiceNumber" — used when qrMode === "field"
    qrFieldName: "", // last segment, e.g. "InvoiceNumber" — used to resolve a sample/real value
    id: id,
    style: {
      position: "absolute",
      top: "40px",
      left: "40px",
      width: "80px",
      height: "80px",
      zIndex: 0
    },
  };
}

export function createImageElement(name) {
  var id = sha256(name + "-img-" + Date.now());
  Date.now();
  return {
    name: name,
    showLogo: false,
    logo: "",
    logoSize: 100,
    id: id,
    style: {
      position: "absolute",
      top: "40px",
      left: "40px",
      height: "60px",
      width: "60px",
    },
  };
}

export function createTableElement(name) {
  var names = name.toString().split(".");
  var id = sha256(name + "-table-" + Date.now());

  return {
    name: name,
    text: "{" + names[names.length - 1] + "}",
    data: names[names.length - 1],
    id: id,
    textAlign: "left",
    width: 10,
    header: names[names.length - 1],
    style: {
      backgroundColor: "transparent",
      /*fontFamily: "Helvetica", color: "#333333FF", fontSize: "12pt"*/
    },
  };
}

export function changeProprty(name, value, props, updateFunc) {
  let properties = {
    ...props.properties,
    [name]: value,
  };
  updateFunc(properties);
}

// export function changeProprtyM(name, value, props, updateFunc) {
//   var names = name.split(".");
//   //console.log(names);
//   let properties = {
//     ...props.properties,
//     [names[0]]: {
//       ...props.properties[names[0]],
//       [names[1]]: names[2]
//         ? {
//             ...props.properties[names[0]][names[1]],
//             [names[2]]: value,
//           }
//         : value,
//     },
//   };
//   updateFunc(properties);
// }

export function changeProprtyM(name, value, props, updateFunc) {
  var names = name.split(".");
  let newProperties;

  if (names.length === 3) {
    newProperties = {
      ...props.properties,
      [names[0]]: {
        ...props.properties[names[0]],
        [names[1]]: {
          ...props.properties[names[0]][names[1]],
          [names[2]]: value,
        },
      },
    };
  } else if (names.length === 4) {
    newProperties = {
      ...props.properties,
      [names[0]]: {
        ...props.properties[names[0]],
        [names[1]]: {
          ...props.properties[names[0]][names[1]],
          [names[2]]: {
            ...props.properties[names[0]][names[1]][names[2]],
            [names[3]]: value,
          },
        },
      },
    };
  } else {
    newProperties = {
      ...props.properties,
      [names[0]]: {
        ...props.properties[names[0]],
        [names[1]]: names[2]
          ? {
            ...props.properties[names[0]][names[1]],
            [names[2]]: value,
          }
          : value,
      },
    };
  }

  console.log("Updating property:", name, "with value:", value, "result:", newProperties);
  updateFunc(newProperties);
}

export function formatDate(date, format, utc) {
  var MMMM = [
    "\x00",
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  var MMM = [
    "\x01",
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  var dddd = [
    "\x02",
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];
  var ddd = ["\x03", "Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  function ii(i, len) {
    var s = i + "";
    len = len || 2;
    while (s.length < len) s = "0" + s;
    return s;
  }

  var y = utc ? date.getUTCFullYear() : date.getFullYear();
  format = format.replace(/(^|[^\\])yyyy+/g, "$1" + y);
  format = format.replace(/(^|[^\\])yy/g, "$1" + y.toString().substr(2, 2));
  format = format.replace(/(^|[^\\])y/g, "$1" + y);

  var M = (utc ? date.getUTCMonth() : date.getMonth()) + 1;
  format = format.replace(/(^|[^\\])MMMM+/g, "$1" + MMMM[0]);
  format = format.replace(/(^|[^\\])MMM/g, "$1" + MMM[0]);
  format = format.replace(/(^|[^\\])MM/g, "$1" + ii(M));
  format = format.replace(/(^|[^\\])M/g, "$1" + M);

  var d = utc ? date.getUTCDate() : date.getDate();
  format = format.replace(/(^|[^\\])dddd+/g, "$1" + dddd[0]);
  format = format.replace(/(^|[^\\])ddd/g, "$1" + ddd[0]);
  format = format.replace(/(^|[^\\])dd/g, "$1" + ii(d));
  format = format.replace(/(^|[^\\])d/g, "$1" + d);

  var H = utc ? date.getUTCHours() : date.getHours();
  format = format.replace(/(^|[^\\])HH+/g, "$1" + ii(H));
  format = format.replace(/(^|[^\\])H/g, "$1" + H);

  var h = H > 12 ? H - 12 : H == 0 ? 12 : H;
  format = format.replace(/(^|[^\\])hh+/g, "$1" + ii(h));
  format = format.replace(/(^|[^\\])h/g, "$1" + h);

  var m = utc ? date.getUTCMinutes() : date.getMinutes();
  format = format.replace(/(^|[^\\])mm+/g, "$1" + ii(m));
  format = format.replace(/(^|[^\\])m/g, "$1" + m);

  var s = utc ? date.getUTCSeconds() : date.getSeconds();
  format = format.replace(/(^|[^\\])ss+/g, "$1" + ii(s));
  format = format.replace(/(^|[^\\])s/g, "$1" + s);

  var f = utc ? date.getUTCMilliseconds() : date.getMilliseconds();
  format = format.replace(/(^|[^\\])fff+/g, "$1" + ii(f, 3));
  f = Math.round(f / 10);
  format = format.replace(/(^|[^\\])ff/g, "$1" + ii(f));
  f = Math.round(f / 10);
  format = format.replace(/(^|[^\\])f/g, "$1" + f);

  var T = H < 12 ? "AM" : "PM";
  format = format.replace(/(^|[^\\])TT+/g, "$1" + T);
  format = format.replace(/(^|[^\\])T/g, "$1" + T.charAt(0));

  var t = T.toLowerCase();
  format = format.replace(/(^|[^\\])tt+/g, "$1" + t);
  format = format.replace(/(^|[^\\])t/g, "$1" + t.charAt(0));

  var tz = -date.getTimezoneOffset();
  var K = utc || !tz ? "Z" : tz > 0 ? "+" : "-";
  if (!utc) {
    tz = Math.abs(tz);
    var tzHrs = Math.floor(tz / 60);
    var tzMin = tz % 60;
    K += ii(tzHrs) + ":" + ii(tzMin);
  }
  format = format.replace(/(^|[^\\])K/g, "$1" + K);

  var day = (utc ? date.getUTCDay() : date.getDay()) + 1;
  format = format.replace(new RegExp(dddd[0], "g"), dddd[day]);
  format = format.replace(new RegExp(ddd[0], "g"), ddd[day]);

  format = format.replace(new RegExp(MMMM[0], "g"), MMMM[M]);
  format = format.replace(new RegExp(MMM[0], "g"), MMM[M]);

  format = format.replace(/\\(.)/g, "$1");

  return format;
}

export function camelize(str) {
  return str
    .split(" ")
    .map(function (word, index) {
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

// Invoice date fields, named here because the invoice definition doesn't
// type them as dates.
const INVOICE_DATE_FIELDS = [
  "EposSalesDefinition.InvoiceDate",
  "EposSalesDefinition.InvoiceDueDate",
  "EposSalesDefinition.DeliveryDate",
];

// Whether a field ("Definition.Property") is a date, so it gets a date format.
export function isDateField(attributes, name) {
  if (!name) return false;
  if (INVOICE_DATE_FIELDS.includes(name)) return true;
  const [definition, property] = name.split(".");
  return attributes?.[definition]?.Properties?.[property] === "date";
}

// TODO: Remove
export function companyPath() {
  return "EposCompanyFieldsDefinition";
}
